import angular from 'angular';
import csmTools from '../../util/tools/tools';
import csmKeycodes from '../../util/keycodes/keycodes';

/** This array contains the indices of the special formatting characters positions. */
var mFormattingIndices = [
  0, // '('
  4, // ')'
  5, // ' '
  9, // '-'
  14, // ' '
  15 // '-' for extension
];

export default angular
  .module('csm.ui.phoneInput', [csmTools, csmKeycodes])
  .directive('phoneInput', Directive).name;

/*******************************************************************************
 * phoneInput */
/**
 * @ngdoc  directive
 * @module csm.ui.phoneInput
 * @name   csm.ui.phoneInput.directive:phoneInput
 * @description
 * For use with text input elements to collect phone numbers as digit only strings
 * for the model and display with formatted characters (ex: (123) 456-7890)
 *******************************************************************************/
/* @ngInject */
function Directive($injector) {
  return {
    restrict: 'A',
    require: 'ngModel',
    link: function (scope, element, attr, ctrl) {
      /*--------------------------------------------------------------------*/
      /* Override $commitViewValue on the ngModelController to update the   */
      /* view while text is being entered. This is needed for format        */
      /* updating as the model value may not change while the input changes */
      /*--------------------------------------------------------------------*/
      var mBaseCommit = ctrl.$commitViewValue.bind(ctrl);
      ctrl.$commitViewValue = function () {
        ctrl.$render();
        mBaseCommit();
      };

      /*--------------------------*/
      /* Set up ngModelController */
      /*--------------------------*/
      ctrl.$parsers.push($injector.invoke(parser));
      ctrl.$render = $injector.invoke(render, null, {
        ctrl: ctrl,
        element: element
      });

      /*---------------------*/
      /* Add Event Listeners */
      /*---------------------*/
      $injector.invoke(keydownListener, null, {
        ctrl: ctrl,
        element: element
      });
      element.on('blur', ctrl.$render);
    }
  };
}

/*******************************************************************************
 * findCaretPosition */
/**
 * Finds and sets the next non-formatting character position for the caret
 * starting at the indicated index.
 *
 * @param element  JQLite Element reference
 * @param idx      Initial cursor index to check
 * @param backward Indication to move backwards(to the left) when true. Forward
 *                 otherwise.
 *******************************************************************************/
function findCaretPosition(element, idx, backward) {
  var text = element.val();
  var dir = backward ? -1 : 1;

  while (
    ((backward && idx > -1) || (!backward && idx < text.length)) &&
    mFormattingIndices.indexOf(idx + dir) > -1
  )
    idx = idx + dir;

  setCaret(element, idx + dir);
}

/*******************************************************************************
 * keydownListener */
/**
 * Registers a keydown listener to handle the delete and backspace keys. This is
 * so formatting characters are ignored and the adjacent digit can be removed.
 *******************************************************************************/
/* @ngInject */
function keydownListener($timeout, KEYCODES, ctrl, element) {
  element.on('keydown', function (event) {
    var isBackspace = event.keyCode == KEYCODES.BACKSPACE;
    var isDelete = event.keyCode == KEYCODES.DELETE;

    /*-------------------*/
    /* Ignore other keys */
    /*-------------------*/
    if (!isBackspace && !isDelete) return true;

    /*---------------------------------------------------------*/
    /* If a range of text is selected, do nothing as the       */
    /* removal will remove all text and trigger a model update */
    /*---------------------------------------------------------*/
    var start = selectionStart(element);
    var end = selectionEnd(element);

    if (start != end) return true;

    /*---------------------------------------*/
    /* Prevent default handling for the keys */
    /*---------------------------------------*/
    event.preventDefault();
    event.stopImmediatePropagation();
    event.cancel = true;

    /*------------------------------------------------------*/
    /* When trying to clear from the beginning of the text, */
    /* just position the cursor at the first input position */
    /*------------------------------------------------------*/
    if (isBackspace && start <= 1) {
      findCaretPosition(element, 0);
      return;
    } else if (isBackspace && mFormattingIndices.indexOf(start - 1) > -1)
      /*-------------------------------------------------*/
      /* For a backspace, if the character to be removed */
      /* is a formating character, adjust the caret      */
      /*-------------------------------------------------*/
      findCaretPosition(element, start, true);
    /*-----------------------------------------------*/
    /* Otherwise for a standard backspace, position  */
    /* the cursor before the character to be removed */
    /*-----------------------------------------------*/ else if (isBackspace)
      setCaret(element, start - 1);
    /*------------------------------------------------------------*/
    /* When a delete is requested against a formatting character, */
    /* find the next available position for a valid drop          */
    /*------------------------------------------------------------*/ else if (
      isDelete &&
      mFormattingIndices.indexOf(start) > -1
    )
      findCaretPosition(element, start);

    /*-----------------------------------*/
    /* Perform the removal of the        */
    /* character at the current position */
    /*-----------------------------------*/
    start = selectionStart(element);
    var text = element.val();
    ctrl.$setViewValue(
      element.val(text.slice(0, start) + text.slice(start + 1)).val()
    );

    /*----------------------------------------------*/
    /* Update the display and reposition again as   */
    /* setting the text changes the cursor position */
    /*----------------------------------------------*/
    ctrl.$render();
    setCaret(element, start);

    /*--------------------------------*/
    /* Prevent default event handling */
    /*--------------------------------*/
    return false;
  });
}

/** Method to remove non-numeric characters from input string. */
function parseInput(input) {
  return input.replace(/\D/g, '');
}

/** Return parser method to get the phone digit string when updating
    the external model value from the input. */
/* @ngInject */
function parser(Tools) {
  return function (value) {
    return Tools.parsePhone(value);
  };
}

/*******************************************************************************
 * render */
/**
 * Formats the text to include appropriate characters with the digits and
 * maintains the cursor position after updating the text.
 *
 * When the element has focus, the currently entered digits will be used
 * otherwise the modelValue is used to display the data.
 *******************************************************************************/
/* @ngInject */
function render(Tools, element, ctrl) {
  return function () {
    var text = element.val() || '';
    var caretIdx = selectionStart(element);
    var beforeCaretDigits = parseInput(text.substring(0, caretIdx)).split('');

    var hasFocus = document.activeElement == element[0];
    var src = hasFocus ? parseInput(text) : ctrl.$modelValue;

    /*--------------------------------------------------------*/
    /* Update the input and viewValue with the formatted text */
    /*--------------------------------------------------------*/
    element.val((text = Tools.formatPhone(src, hasFocus)));
    ctrl.$viewValue = text;

    /*--------------------------------------------*/
    /* Adjust the caret positioning when in focus */
    /*--------------------------------------------*/
    if (hasFocus) {
      caretIdx = -1;
      beforeCaretDigits.forEach(function (digit) {
        do {
          caretIdx++;
        } while (caretIdx < text.length && text.charAt(caretIdx) != digit);
      });

      findCaretPosition(element, caretIdx);
    }
  };
}

/** Retrieves the end caret index position for a jQlite element. */
function selectionEnd(element) {
  return element[0].selectionEnd;
}

/** Retrieves the start caret index position for a jQlite element. */
function selectionStart(element) {
  return element[0].selectionStart;
}

/** Set the caret position for the provided element and idx. */
function setCaret(element, pos) {
  element[0].setSelectionRange(pos, pos);
}
